import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

const SITE_URL = process.env.REACT_APP_SITE_URL || "https://coteperdue.com";
const SITE_NAME = "Côte Perdue";
const DEFAULT_IMAGE = `${SITE_URL}/api/static/img/hero_train_mers_1902_v2.jpg`;

const DEFAULT_DESC_FR = "Côte Perdue — magazine et carnet d'adresses vérifiées entre Haute-Normandie et Picardie : Le Tréport, Mers-les-Bains, Cayeux-sur-Mer, Eu, Bois-de-Cise, Blangy-sur-Bresle, Aumale, Neufchâtel-en-Bray. Hôtels chic, restaurants notés Google ≥ 4,5/5, randonnées, jardins remarquables, châteaux.";
const DEFAULT_DESC_EN = "Côte Perdue — editorial magazine and verified address book of the Norman & Picardy coast: Le Tréport, Mers-les-Bains, Cayeux-sur-Mer, Eu, Bois-de-Cise, Aumale. Refined hotels, Google-rated ≥ 4.5/5 restaurants, walks, remarkable gardens, châteaux.";

const DEFAULT_KEYWORDS_FR = [
    "Normandie", "Picardie", "Le Tréport", "Mers-les-Bains", "Cayeux-sur-Mer", "Eu",
    "Blangy-sur-Bresle", "Bois-de-Cise", "Ault", "Aumale", "Neufchâtel-en-Bray",
    "Côte d'Albâtre", "Baie de Somme", "vallée de la Bresle",
    "falaises de craie", "villas Belle Époque", "Château d'Eu", "Manoir d'Ango",
    "Forêt d'Eu", "week-end Normandie", "hôtel chic Normandie",
];
const DEFAULT_KEYWORDS_EN = [
    "Normandy", "Picardy", "Le Tréport", "Mers-les-Bains", "Cayeux-sur-Mer", "Eu",
    "Côte d'Albâtre", "Bay of Somme", "Bresle Valley",
    "chalk cliffs", "Belle Époque villas", "Château d'Eu",
    "Forêt d'Eu", "Normandy weekend", "chic Normandy hotel",
    "France hidden coast", "off-the-beaten-path Normandy",
];

export default function SEO({
    title,
    description,
    image,
    path = "",
    keywords,
    type = "website",
    structuredData,
    noindex = false,
}) {
    const { i18n } = useTranslation();
    const lang = (i18n.language || "fr").startsWith("en") ? "en" : "fr";
    const htmlLang = lang === "fr" ? "fr-FR" : "en";

    const fullTitle = title
        ? `${title} · ${SITE_NAME}`
        : lang === "en"
            ? `${SITE_NAME} — The chic, hidden coast of Normandy & Picardy`
            : `${SITE_NAME} — La côte chic discrète de Normandie & Picardie`;
    const desc = description || (lang === "en" ? DEFAULT_DESC_EN : DEFAULT_DESC_FR);
    const url = `${SITE_URL}${path}${lang === "en" ? (path.includes("?") ? "&lang=en" : "?lang=en") : ""}`;
    const altUrlFr = `${SITE_URL}${path}`;
    const altUrlEn = `${SITE_URL}${path}${path.includes("?") ? "&" : "?"}lang=en`;
    const ogImage = image
        ? (image.startsWith("http") ? image : `${SITE_URL}${image}`)
        : DEFAULT_IMAGE;
    const baseKeywords = lang === "en" ? DEFAULT_KEYWORDS_EN : DEFAULT_KEYWORDS_FR;
    const kw = (keywords && keywords.length ? keywords : baseKeywords).join(", ");

    return (
        <Helmet>
            <html lang={htmlLang} />
            <title>{fullTitle}</title>
            <meta name="description" content={desc} />
            <meta name="keywords" content={kw} />
            {noindex
                ? <meta name="robots" content="noindex,nofollow" />
                : <meta name="robots" content="index,follow,max-image-preview:large" />
            }
            <link rel="canonical" href={url} />
            <link rel="alternate" hrefLang="fr" href={altUrlFr} />
            <link rel="alternate" hrefLang="en" href={altUrlEn} />
            <link rel="alternate" hrefLang="x-default" href={altUrlFr} />

            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:locale" content={lang === "en" ? "en_GB" : "fr_FR"} />
            {lang === "en" && <meta property="og:locale:alternate" content="fr_FR" />}
            {lang === "fr" && <meta property="og:locale:alternate" content="en_GB" />}
            <meta property="og:type" content={type} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={ogImage} />

            <meta name="geo.region" content="FR-NOR" />
            <meta name="geo.placename" content="Le Tréport, Mers-les-Bains, Cayeux-sur-Mer" />
            <meta name="geo.position" content="50.0567;1.3760" />
            <meta name="ICBM" content="50.0567, 1.3760" />

            {structuredData && (
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            )}
        </Helmet>
    );
}

export const orgLD = () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": SITE_NAME,
    "url": SITE_URL,
    "logo": `${SITE_URL}/logo512.png`,
    "description": DEFAULT_DESC_FR,
    "areaServed": {
        "@type": "AdministrativeArea",
        "name": "Normandie & Picardie — côte du Tréport à Cayeux-sur-Mer",
    },
});

export const websiteLD = () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": SITE_NAME,
    "url": SITE_URL,
    "description": DEFAULT_DESC_FR,
    "inLanguage": ["fr-FR", "en"],
    "potentialAction": {
        "@type": "SearchAction",
        "target": `${SITE_URL}/adresses?q={search_term_string}`,
        "query-input": "required name=search_term_string",
    },
});

export const placeLD = (place) => ({
    "@context": "https://schema.org",
    "@type": place.category === "hotel" || place.category === "maison-hotes" ? "LodgingBusiness" :
              place.category === "restaurant" ? "Restaurant" :
              place.category === "musee" ? "Museum" :
              "LocalBusiness",
    "name": place.name,
    "image": place.image && (place.image.startsWith("http") ? place.image : `${SITE_URL}${place.image}`),
    "description": place.short_description || place.description,
    "address": {
        "@type": "PostalAddress",
        "addressLocality": place.city_slug,
        "addressRegion": "Normandie / Picardie",
        "addressCountry": "FR",
        "streetAddress": place.address || "",
    },
    ...(place.lat && place.lng ? {
        "geo": {"@type": "GeoCoordinates", "latitude": place.lat, "longitude": place.lng},
    } : {}),
    ...(place.google_rating ? {
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": place.google_rating,
            "reviewCount": place.google_review_count || 0,
            "bestRating": 5,
        },
    } : {}),
    ...(place.phone ? {"telephone": place.phone} : {}),
    ...(place.website ? {"url": place.website} : {}),
    ...(place.price_range ? {"priceRange": place.price_range} : {}),
});

export const breadcrumbLD = (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((it, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": it.name,
        "item": `${SITE_URL}${it.path}`,
    })),
});

export { SITE_URL };
